import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import api, { apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { ScrollText, CheckCircle2, ShieldAlert, CircleDot, Sparkles, Filter } from "lucide-react";
import { toast } from "sonner";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";

function shortHash(h) {
  if (!h) return "-";
  return `${h.slice(0, 10)}…${h.slice(-6)}`;
}

function formatDate(iso) {
  try {
    return new Date(iso).toLocaleString("fr-FR", {
      year: "numeric", month: "2-digit", day: "2-digit",
      hour: "2-digit", minute: "2-digit", second: "2-digit",
    });
  } catch { return iso; }
}

export default function AuditLog() {
  const { t } = useTranslation();
  const { session } = useAuth();
  const [entries, setEntries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [chain, setChain] = useState(null);
  const [actorFilter, setActorFilter] = useState("all");

  const load = async () => {
    setLoading(true);
    try {
      const params = { limit: 200 };
      if (actorFilter !== "all") params.actor_type = actorFilter;
      const { data } = await api.get("/tenant/audit-log", { params });
      setEntries(data.entries);
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setLoading(false);
    }
  };

  const verify = async () => {
    try {
      const { data } = await api.get("/tenant/audit-log/verify");
      setChain(data);
    } catch (e) { toast.error(apiError(e)); }
  };

  useEffect(() => { load(); }, [session?.tenant?.id, actorFilter]);

  return (
    <div className="space-y-6" data-testid="audit-page">
      <div className="flex items-start justify-between gap-4">
        <div>
          <h1 className="text-3xl font-semibold tracking-tight text-slate-900"
              style={{ fontFamily: "Manrope, sans-serif" }}>
            {t("audit.title")}
          </h1>
          <p className="mt-1 text-sm text-slate-500">{t("audit.subtitle")}</p>
        </div>
        <div className="flex items-center gap-2">
          <Select value={actorFilter} onValueChange={setActorFilter}>
            <SelectTrigger className="w-56" data-testid="audit-actor-filter">
              <Filter className="w-3.5 h-3.5 mr-1.5 text-slate-500" />
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all" data-testid="filter-all">Tous les acteurs</SelectItem>
              <SelectItem value="user" data-testid="filter-user">👤 Utilisateurs</SelectItem>
              <SelectItem value="assistant" data-testid="filter-assistant">✨ Averdy Agency IA</SelectItem>
              <SelectItem value="system" data-testid="filter-system">⚙ Système</SelectItem>
              <SelectItem value="staff" data-testid="filter-staff">🛠 Staff</SelectItem>
            </SelectContent>
          </Select>
          <Button onClick={verify}
                  variant="outline"
                  data-testid="verify-chain-btn"
                  className="border-slate-200 text-slate-700">
            <ShieldAlert className="h-4 w-4 mr-1.5" />
            {t("audit.verifyBtn")}
          </Button>
        </div>
      </div>

      {chain && (
        <Alert
          data-testid="chain-result"
          variant={chain.ok ? "default" : "destructive"}
          className={chain.ok ? "border-green-200 bg-green-50 text-green-800" : ""}
        >
          {chain.ok
            ? <CheckCircle2 className="h-4 w-4" />
            : <ShieldAlert className="h-4 w-4" />}
          <AlertDescription>
            {chain.ok
              ? t("audit.chainOk", { n: chain.checked })
              : t("audit.chainBroken", { seq: chain.broken_at, reason: chain.reason })}
          </AlertDescription>
        </Alert>
      )}

      <Card className="border-slate-200 bg-white overflow-hidden rounded-xl">
        {loading ? (
          <div className="p-10 text-center text-slate-500 text-sm">{t("common.loading")}</div>
        ) : entries.length === 0 ? (
          <div className="p-16 text-center">
            <ScrollText className="h-8 w-8 text-slate-300 mx-auto" />
            <p className="mt-3 text-sm text-slate-500">{t("audit.emptyState")}</p>
          </div>
        ) : (
          <Table data-testid="audit-table">
            <TableHeader>
              <TableRow>
                <TableHead className="w-16">{t("audit.col.seq")}</TableHead>
                <TableHead>{t("audit.col.action")}</TableHead>
                <TableHead>{t("audit.col.actor")}</TableHead>
                <TableHead>{t("audit.col.target")}</TableHead>
                <TableHead>{t("audit.col.time")}</TableHead>
                <TableHead className="text-right">{t("audit.col.hash")}</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {entries.map((e) => (
                <TableRow key={e.id} data-testid={`audit-row-${e.seq}`}>
                  <TableCell className="text-slate-400 font-mono text-xs">#{e.seq}</TableCell>
                  <TableCell>
                    <div className="flex items-center gap-1.5">
                      <Badge variant="outline"
                             className={e.actor_type === "assistant"
                               ? "border-purple-300 bg-purple-50 text-purple-700 font-mono text-[11px]"
                               : "border-slate-200 text-slate-700 font-mono text-[11px]"}>
                        {e.action}
                      </Badge>
                      {e.actor_type === "assistant" && (
                        <Badge className="bg-purple-100 text-purple-800 hover:bg-purple-100 text-[10px]"
                                data-testid={`ai-badge-${e.seq}`}>
                          <Sparkles className="w-2.5 h-2.5 mr-0.5" />IA autonome
                        </Badge>
                      )}
                    </div>
                  </TableCell>
                  <TableCell className="text-sm">
                    <div className="flex items-center gap-2">
                      {e.actor_type === "assistant"
                        ? <Sparkles className="h-3 w-3 text-purple-500" />
                        : <CircleDot className={
                            e.actor_type === "staff" ? "h-3 w-3 text-amber-500"
                            : e.actor_type === "system" ? "h-3 w-3 text-slate-300"
                            : "h-3 w-3 text-blue-500"
                          } />}
                      <div>
                        <div className="text-slate-800">{e.actor_email || "—"}</div>
                        <div className="text-[10px] uppercase tracking-widest text-slate-400">
                          {e.actor_type === "assistant" ? "Averdy Agency IA" : e.actor_type}
                        </div>
                      </div>
                    </div>
                  </TableCell>
                  <TableCell className="text-sm text-slate-600 max-w-xs truncate">
                    {e.target || "—"}
                  </TableCell>
                  <TableCell className="text-sm text-slate-500 whitespace-nowrap">
                    {formatDate(e.timestamp)}
                  </TableCell>
                  <TableCell className="text-right font-mono text-[10px] text-slate-400">
                    {shortHash(e.hash)}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </Card>
    </div>
  );
}
